/**
 * See360 Embeddable 360° Viewer Widget
 *
 * QUICK START (2 lines):
 *   <script src="https://cdn.see360.ai/viewer/v1/see360.js" data-api-key="YOUR_API_KEY"></script>
 *   <div class="see360-viewer" data-vin="YOUR_VIN_HERE"></div>
 *
 * LOOKUP OPTIONS (pick one per div):
 *   data-vin="USXYZ12345H607"     — lookup by VIN/HIN
 *   data-stock="STK-1234"         — lookup by stock number
 *   data-vehicle-id="uuid-..."    — lookup by See360 vehicle ID
 *
 * GALLERY OPTIONS:
 *   data-gallery="true"                — enable image gallery ribbon
 *   data-gallery-thumb-width="100"     — thumbnail width in px (default: 80)
 *   data-gallery-thumb-height="75"     — thumbnail height in px (default: 60)
 *   data-gallery-bg="#1a1a2e"          — ribbon background color (default: #0a0a0a)
 *
 * API KEY:
 *   Preferred: set data-api-key on the <script> tag (once per site)
 *   Alternative: set data-api-key on each viewer <div>
 */

import { Viewer, type ViewerConfig } from '@see360/viewer-core';

// ─── Preconnect to CDN and API for faster first request ─

(function injectPreconnect() {
  const domains = ['https://cdn.see360.ai', 'https://api.see360.ai'];
  for (const href of domains) {
    if (!document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  }
})();

// ─── Discover API key from <script> tag or container ─

function getApiKey(container: HTMLElement): string | null {
  // 1. Check the container div itself
  if (container.dataset.apiKey) return container.dataset.apiKey;

  // 2. Check the <script> tag that loaded us
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[data-api-key]'
  );
  for (const script of scripts) {
    if (script.dataset.apiKey) return script.dataset.apiKey;
  }

  return null;
}

// ─── Discover API URL ─

function getApiUrl(container: HTMLElement): string {
  // Check container, then script tag, then default
  if (container.dataset.apiUrl) return container.dataset.apiUrl;

  const script = document.querySelector<HTMLScriptElement>('script[data-api-url]');
  if (script?.dataset.apiUrl) return script.dataset.apiUrl;

  return 'https://api.see360.ai';
}

// ─── Build the lookup query string ─

function buildLookupQuery(container: HTMLElement): string | null {
  const vin = container.dataset.vin;
  const stock = container.dataset.stock;
  const vehicleId = container.dataset.vehicleId;

  if (vin) return `vin=${encodeURIComponent(vin)}`;
  if (stock) return `stock=${encodeURIComponent(stock)}`;
  if (vehicleId) return `vehicleId=${encodeURIComponent(vehicleId)}`;

  return null;
}

// ─── Inject scoped styles (once) ─

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes see360-spin { to { transform: rotate(360deg); } }
    .see360-viewer {
      position: relative;
      overflow: hidden;
      background: #111;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .see360-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      min-height: 400px;
      color: #fff;
    }
    .see360-loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255,255,255,0.2);
      border-top-color: #00d4ff;
      border-radius: 50%;
      animation: see360-spin 1s linear infinite;
      margin: 0 auto 16px;
    }
    .see360-error {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      min-height: 400px;
      color: #fff;
      text-align: center;
      padding: 20px;
    }
    .see360-error-icon { font-size: 24px; margin-bottom: 8px; }
    .see360-error-detail { opacity: 0.5; font-size: 13px; margin-top: 8px; }
    .see360-badge {
      position: absolute;
      bottom: 8px;
      left: 8px;
      font-size: 10px;
      color: rgba(255,255,255,0.4);
      z-index: 30;
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
}

// ─── Show loading state ─

function showLoading(container: HTMLElement): void {
  container.innerHTML = `
    <div class="see360-loading">
      <div style="text-align:center">
        <div class="see360-loading-spinner"></div>
        <p style="opacity:0.7">Loading 360° viewer…</p>
      </div>
    </div>
  `;
}

// ─── Show error state ─

function showError(container: HTMLElement, message: string): void {
  container.innerHTML = `
    <div class="see360-error">
      <div>
        <div class="see360-error-icon">⚠️</div>
        <p>Unable to load 360° viewer</p>
        <p class="see360-error-detail">${message}</p>
      </div>
    </div>
  `;
}

// ─── Initialize a single viewer instance ─

async function initViewer(container: HTMLElement): Promise<void> {
  injectStyles();

  const apiKey = getApiKey(container);
  const apiUrl = getApiUrl(container);
  const lookupQuery = buildLookupQuery(container);

  if (!apiKey) {
    console.error('[See360] Missing API key. Set data-api-key on the <script> tag or viewer <div>.');
    showError(container, 'Missing API key configuration.');
    return;
  }

  if (!lookupQuery) {
    console.error('[See360] Missing vehicle identifier. Set data-vin, data-stock, or data-vehicle-id on the viewer <div>.');
    showError(container, 'Missing vehicle identifier (data-vin or data-stock).');
    return;
  }

  showLoading(container);

  try {
    const response = await fetch(
      `${apiUrl}/embed/spin?${lookupQuery}`,
      {
        headers: { 'X-Api-Key': apiKey },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();

    // Clear loading state
    container.innerHTML = '';

    // Initialize the viewer engine
    const galleryEnabled = container.dataset.gallery === 'true';
    const galleryImages = galleryEnabled && data.gallery?.length > 0 ? data.gallery : undefined;

    // Gallery customization
    const galleryThumbWidth = container.dataset.galleryThumbWidth ? parseInt(container.dataset.galleryThumbWidth, 10) : undefined;
    const galleryThumbHeight = container.dataset.galleryThumbHeight ? parseInt(container.dataset.galleryThumbHeight, 10) : undefined;
    const galleryBgColor = container.dataset.galleryBg || undefined;

    const viewer = new Viewer({
      container,
      cdnBaseUrl: data.cdnBaseUrl,
      cdnBasePath: data.spin.cdnBasePath,
      imagePattern: data.spin.imagePattern,
      frameCount: data.spin.exteriorFrameCount,
      hotspots: data.hotspots,
      interiorPath: data.spin.interiorPath,
      interiorFrameCount: data.spin.interiorFrameCount || 0,
      interiorImagePattern: data.spin.interiorImagePattern || 'interior_{index}.jpg',
      interiorStatus: data.spin.interiorStatus || 'NONE',
      interiorType: data.spin.interiorType || 'FRAMES',
      apiUrl,
      vehicleId: data.vehicle.id,
      autoplay: true,
      autoplaySpeed: 200,
      gallery: galleryImages,
      galleryThumbWidth: galleryThumbWidth && galleryThumbWidth > 0 ? galleryThumbWidth : undefined,
      galleryThumbHeight: galleryThumbHeight && galleryThumbHeight > 0 ? galleryThumbHeight : undefined,
      galleryBgColor,
      analytics: {
        apiUrl,
        apiKey,
        spin360Id: data.spin.id,
      },
      onReady: () => {
        console.log(`[See360] Viewer ready: ${data.vehicle.year} ${data.vehicle.make} ${data.vehicle.model}${galleryImages ? ` (${galleryImages.length} gallery images)` : ''}`);
      },
    });

    // Add "Powered by" badge
    const badge = document.createElement('div');
    badge.className = 'see360-badge';
    badge.textContent = 'Powered by See360';
    container.appendChild(badge);

    // Store viewer instance for external access
    (container as any).__see360Viewer = viewer;

  } catch (error) {
    console.error('[See360] Failed to initialize viewer:', error);
    showError(container, (error as Error).message);
  }
}

// ─── Auto-initialize all viewer containers on page load ─

function autoInit(): void {
  const containers = document.querySelectorAll<HTMLElement>(
    '.see360-viewer, [data-see360], #see360-viewer'
  );
  containers.forEach(initViewer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}

// ─── Public API ─

const See360 = {
  init: initViewer,
  version: '1.0.0',
};

// Expose globally for IIFE bundle
(window as any).See360 = See360;

export { See360 };
